import React from 'react';
import MaterialTable from 'material-table';
import moment from 'moment';
import { Add } from '@material-ui/icons';
import {
  Button,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import {
  getTblTarif,
  postTblTarif,
  putTblTarif,
  deleteTblTarif,
} from '../../../services';
import Context from '../../../context';

const initTarifState = {
  id: '',
  asal: '',
  tujuan: '',
  golongan: '',
  tarif: '',
  keterangan: '',
};

function isSpecialChar(str: any) {
  // eslint-disable-next-line no-useless-escape
  const format = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
  return format.test(str);
}

const CrudTarif = ({ tahap }: any) => {
  const context = React.useContext(Context);
  const tableRef: any = React.createRef();
  const { enqueueSnackbar } = useSnackbar();
  const [loadingState, setLoadingState] = React.useState(false);
  const [actionFormState, setActionFormState] = React.useState('');
  const [refreshTableState, setRefreshTableState] = React.useState(0);
  const [selectTarifState, setSelectTarifState] =
    React.useState(initTarifState);
  const { id, asal, tujuan, golongan, tarif, keterangan } = selectTarifState;

  const refreshTable = () => {
    tableRef.current && tableRef.current.onQueryChange();
  };

  const handleChange = (event: any, isGolongan = false) => {
    if (isGolongan) {
      setSelectTarifState({
        ...selectTarifState,
        golongan: event.target.value,
      });
    } else {
      setSelectTarifState({
        ...selectTarifState,
        [event.target.name]: event.target.value,
      });
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    let status;
    let message;
    try {
      setLoadingState(true);
      if (isSpecialChar(asal) || isSpecialChar(tujuan)) {
        throw new Error('no special character');
      }
      if (actionFormState === 'Add') {
        const res = await postTblTarif(selectTarifState);
        status = res.status;
        message = res.message;
      }
      if (actionFormState === 'Edit') {
        const res = await putTblTarif(id, selectTarifState);
        status = res.status;
        message = res.message;
      }
      setLoadingState(false);
      if (status === 'success') {
        setActionFormState('');
        setSelectTarifState(initTarifState);
      }
      enqueueSnackbar(message, {
        variant: status,
      });
    } catch (error: any) {
      setLoadingState(false);
      enqueueSnackbar(error.message, {
        variant: 'error',
      });
    }
  };

  const handleDelete = (idTarif: any) => {
    try {
      context.openDialog(
        'Hapus Data',
        `Apakah anda yakin ingin menghapus data id ${idTarif}?`,
        async () => {
          const res = await deleteTblTarif(idTarif);
          if (res.status === 'error') {
            enqueueSnackbar(res.message, {
              variant: 'error',
            });
          }
          if (res.status === 'success') {
            setRefreshTableState(refreshTableState + 1);
            context.closeDialog();
            enqueueSnackbar(`Berhasil menghapus data id ${idTarif}`, {
              variant: 'success',
            });
          }
        }
      );
    } catch (err: any) {
      enqueueSnackbar(err.message, {
        variant: 'error',
      });
    }
  };

  React.useEffect(() => {
    refreshTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTableState]);

  if (actionFormState) {
    return (
      <form
        noValidate
        onSubmit={handleSubmit}
        autoComplete='off'
        style={{ display: 'flex', flexDirection: 'column', maxWidth: 400 }}
      >
        <Grid container direction='column' spacing={3}>
          <Grid item>
            <Typography color='primary' variant='h4'>
              {actionFormState} Tarif{' '}
              {actionFormState === 'Edit' && ' Id ' + id}
            </Typography>
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              name='asal'
              value={asal}
              onChange={handleChange}
              label='Asal'
              variant='outlined'
            />
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              name='tujuan'
              value={tujuan}
              onChange={handleChange}
              label='Tujuan'
              variant='outlined'
            />
          </Grid>
          <Grid item>
            <FormControl variant='outlined' style={{ width: '100%' }}>
              <InputLabel id='gol'>Gologan Kendaraan</InputLabel>
              <Select
                fullWidth
                labelId='gol'
                id='gol'
                value={golongan}
                onChange={(e) => handleChange(e, true)}
                label='Gologan Kendaraan'
                required
              >
                {[1, 2, 3, 4, 5].map((gol, i) => (
                  <MenuItem key={i} value={gol}>
                    {gol}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              type='number'
              name='tarif'
              value={tarif}
              onChange={handleChange}
              label='Tarif'
              variant='outlined'
            />
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              name='keterangan'
              value={keterangan}
              onChange={handleChange}
              label='Keterangan'
              variant='outlined'
            />
          </Grid>
          <Grid
            item
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}
          >
            <Button
              variant='contained'
              size='large'
              disabled={loadingState}
              onClick={() => {
                setSelectTarifState(initTarifState);
                setActionFormState('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='large'
              type='submit'
              disabled={loadingState}
            >
              {loadingState ? 'Loading' : 'Save'}
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '1rem',
        }}
      >
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={() => setActionFormState('Add')}
          startIcon={<Add />}
        >
          Add
        </Button>
      </div>
      <MaterialTable
        tableRef={tableRef}
        columns={[
          { title: 'Id', field: 'id' },
          { title: 'Asal', field: 'asal' },
          { title: 'Tujuan', field: 'tujuan' },
          { title: 'Golongan', field: 'golongan' },
          { title: 'Tarif', field: 'tarif' },
          { title: 'Keterangan', field: 'keterangan' },
          {
            title: 'UpdatedAt',
            field: 'updated_at',
            align: 'right',
            render: (rowData) =>
              moment(rowData.updated_at).format('YYYY-MM-DD HH:mm'),
          },
          {
            title: 'CreatedAt',
            field: 'created_at',
            align: 'right',
            render: (rowData) =>
              moment(rowData.created_at).format('YYYY-MM-DD HH:mm'),
          },
        ]}
        title={
          <Typography variant='h6' color='primary' style={{ fontWeight: 600 }}>
            List Tarif
          </Typography>
        }
        data={(query) =>
          new Promise((resolve) => {
            const { pageSize, page, search } = query;
            getTblTarif(pageSize, page + 1, search, tahap).then(
              (result: any) => {
                resolve({
                  data: result.data.rows,
                  page: Number(result.data.page - 1),
                  totalCount: Number(result.data.total),
                });
              }
            );
          })
        }
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 25, 50],
          actionsColumnIndex: -1,
        }}
        actions={[
          {
            icon: 'edit',
            tooltip: 'Edit Tarif',
            onClick: (event, rowData: any) => {
              setSelectTarifState(rowData);
              setActionFormState('Edit');
            },
          },
          (rowData) => ({
            icon: 'delete',
            tooltip: 'Delete Tarif',
            onClick: (event, rowData: any) => {
              handleDelete(rowData.id);
            },
          }),
          {
            icon: 'refresh',
            tooltip: 'Refresh Data',
            isFreeAction: true,
            onClick: () => refreshTable(),
          },
        ]}
      />
    </>
  );
};

export default CrudTarif;
